import React, { useEffect, useState, useContext } from "react";
import {
  Typography,
  Input,
  Button,
  Dropdown,
  Menu,
  Icon,
  message,
  Spin,
  Table,
  Tag,
  DatePicker,
  Modal,
} from "antd";
import { platformApi } from "../../../api";
import { ContextAPI } from "../../../ContextAPI";
import { DownloadOutlined } from "@ant-design/icons";
import { ENDPOINT } from "../../../constants";

const { Title } = Typography;
const { Search } = Input;
const { Item } = Menu;
const { RangePicker } = DatePicker;

export default function Payment() {
  const [data, setData] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [denoModalVisible, setDenoModalVisible] = useState(false);
  const [selectedDenominations, setSelectedDenominations] = useState([]);
  const { loginCredintials } = useContext(ContextAPI);

  const denoColumns = [
    { title: "Denomination (₹)", dataIndex: "denomination", key: "denomination", render: (val) => (val ? `₹${val}` : "-") },
    { title: "Count", dataIndex: "count", key: "count" },
    { title: "Amount (₹)", dataIndex: "amount", key: "amount", render: (val) => (val ? `₹${val.toLocaleString()}` : "-") },
  ];

  const getAllPayments = async (pageNum = 1, pageSize = 10, searchValue = null, dates = dateRange) => {
    setSpinning(true);
    try {
      const branchData = localStorage.getItem('branch');
      const branchIds = branchData ? JSON.parse(branchData).map(branch => branch.id) : [];

      const payload = {
        page: pageNum,
        size: pageSize,
        branchId: branchIds,
        searchString: searchValue || "",
      };

      if (dates && dates[0] && dates[1]) {
        payload.startDate = dates[0].format("YYYY-MM-DD");
        payload.endDate = dates[1].format("YYYY-MM-DD");
      }

      const res = await platformApi.post("/api/payment/getAll", payload);

      const { data } = res;
      if (data.code === 200 && data.response?.code === 200) {
        const payments = data.response.data.payments || [];
        setData(payments);
        setCount(data.response.data.count || payments.length);
      } else {
        message.error("Failed to fetch payments");
      }
    } catch (err) {
      console.error("Error fetching payments:", err);
      message.error("Error fetching payments");
    } finally {
      setSpinning(false);
    }
  };

  useEffect(() => {
    getAllPayments(page, limit, search, dateRange);
  }, [page, limit, search, dateRange]);

  // Dropdown menu for page size
  const getMenu = () => {
    const size = Math.min(10, Math.ceil(count / 10));
    const options = [10];
    for (let i = 2; i <= size; i++) options.push(i * 10);
    return options.map((menu) => (
      <Item
        key={menu}
        onClick={() => {
          setLimit(menu);
          setPage(1);
        }}
      >
        {menu}
      </Item>
    ));
  };

  const menu = <Menu>{getMenu()}</Menu>;

  const columns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      render: (text) => {
        if (!text) return "-";
        const trimmed = text.trim();
        if (trimmed === "BOOKING_REGISTER") return "Booking";
        if (trimmed === "CSO") return "CSO";
        
        return trimmed
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
      },
    },
    {
      title: "ID",
      dataIndex: "bookingId",
      key: "bookingId",
      render: (text) => text || "-",
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      render: (text, record) => text?.trim() || record.paidBy?.name || "-",
    },
    // {
    //   title: "Bill Amount",
    //   dataIndex: "billAmount",
    //   key: "billAmount",
    //   render: (val) => (val ? `₹${val.toLocaleString()}` : "-"),
    // },
    {
      title: "Amount",
      dataIndex: "collectedAmount",
      key: "collectedAmount",
      render: (val) => (val ? `₹${val.toLocaleString()}` : "₹0"),
    },
    {
      title: "Collected By",
      dataIndex: "collectedBy",
      key: "collectedBy",
      render: (val) => (val ? val.employeeName : "-" ),
    },
    {
      title: "Payment Mode",
      dataIndex: "method",
      key: "method",
      render: (text) => text ? text.replace(/_/g, " ") : "-",
    },
    {
      title: "Bank",
      dataIndex: "bank",
      key: "bank",
      render: (text) => (text ? text.branchName || text.name || "-" : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color =
          status === "SUCCESS"
            ? "green"
            : status === "PENDING"
            ? "orange"
            : "red";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Denominations",
      key: "denominations",
      render: (record) => {
        const isCash = record.method && record.method.trim().toUpperCase() === "CASH";
        if (!isCash) return "-";
        return (
          <span
            style={{ color: "#006666", textDecoration: "underline", fontWeight: 500, cursor: "pointer" }}
            onClick={() => {
              setSelectedDenominations(record.denominations || []);
              setDenoModalVisible(true);
            }}
          >
            View
          </span>
        );
      },
    },
    {
      title: "Date",
      key: "paymentDate",
      render: (_, record) => {
        const dateVal = record.dateOfPayment || record.createdAt;
        return dateVal ? new Date(dateVal).toLocaleString("en-IN") : "-";
      },
    },
    {
      title: "Receipt",
      key: "download",
      align: "center",
      render: (record) => {
        const moduleName = record.module?.trim()?.toUpperCase() || "";
        const hiddenModules = ["BOOKING_PAYMENT"];

        if (hiddenModules.includes(moduleName)) {
          return null;
        }

        const url =
          record.module?.trim() === "JOB_INVOICE"
            ? `${ENDPOINT}api/payment/jobInvoice/receipt/${record.id}?group=false`
            : `${ENDPOINT}api/payment/${record.module?.trim().toLowerCase()}/receipt/${record.id}?group=false`;

        return (
          record.status === "SUCCESS" ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title="Download Payment PDF"
            >
              <DownloadOutlined style={{ fontSize: 18, color: "#1890ff" }} />
            </a>
          ) : null
        );
      },
    },
  ];

  return (
    <div>
      {/* ===== Header Section ===== */}
      <div className="accessories-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title style={{ width: "40%", margin: 0 }} level={4}>
          Receipts [{count}]
          <span style={{ marginLeft: ".5rem" }}>
            <Dropdown overlay={menu} trigger={["click"]}>
              <Button>
                {limit} <Icon type="down" />
              </Button>
            </Dropdown>
          </span>
        </Title>

        <div style={{ display: "flex", gap: "12px", alignItems: "center", width: "55%", justifyContent: "flex-end" }}>
          <RangePicker
            format="DD/MM/YYYY"
            onChange={(dates) => {
              setPage(1);
              setDateRange(dates && dates.length === 2 ? dates : [null, null]);
            }}
            style={{ width: "230px" }}
          />

          <Search
            style={{ width: "220px" }}
            placeholder="Search Payment"
            onSearch={(value) => {
              setPage(1);
              setSearch(value);
            }}
            onChange={(e) => {
              if (e.target.value === "") {
                setSearch(null);
                setPage(1);
              }
            }}
          />
        </div>
      </div>

      {/* ===== Table Section ===== */}
      <Spin spinning={spinning}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          pagination={{
            current: page,
            total: count,
            pageSize: limit,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
        />
      </Spin>

      {/* ===== Denomination Modal ===== */}
      <Modal
        title="Denomination Details"
        visible={denoModalVisible}
        onCancel={() => setDenoModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDenoModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={500}
      >
        <Table
          columns={denoColumns}
          dataSource={selectedDenominations}
          rowKey={(r, index) => r.id || index}
          pagination={false}
        />
      </Modal>
    </div>
  );
}
